import React from 'react';
import { COUNTRIES } from '../../constants/countries';
import type { Customer } from '../../types/domain';
import type { CreateCustomerInput, UpdateCustomerInput } from '../../types/api';
import Button from '../common/Button';

interface CustomerFormProps {
  customer?: Customer;
  onSubmit: (data: CreateCustomerInput | UpdateCustomerInput) => void;
  onCancel: (e?: React.MouseEvent) => void;
  isSubmitting?: boolean;
}

export const CustomerForm: React.FC<CustomerFormProps> = ({
  customer,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  console.log('[CustomerForm] Rendering with props:', { customer, isSubmitting });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    console.log('[CustomerForm] Form submit event triggered');
    e.preventDefault();
    e.stopPropagation();
    
    const form = e.currentTarget;
    console.log('[CustomerForm] Form validity:', form.checkValidity());
    
    const formData = new FormData(form);
    const rawData = Object.fromEntries(formData);
    console.log('[CustomerForm] Raw form data:', rawData);
    
    if (!rawData.name || !rawData.country) {
      console.error('[CustomerForm] Required fields missing:', { name: rawData.name, country: rawData.country });
      return;
    }
    
    const data = {
      name: formData.get('name') as string,
      contactName: formData.get('contactName') as string || undefined,
      email: (formData.get('email') as string) || undefined,
      phone: (formData.get('phone') as string) || undefined,
      country: formData.get('country') as string,
    };

    console.log('[CustomerForm] Processed form data:', data);

    // Filter out undefined values
    const cleanedData = Object.fromEntries(
      Object.entries(data).filter(([_, value]) => value !== undefined && value !== '')
    );

    console.log('[CustomerForm] Cleaned form data:', cleanedData);
    
    try {
      onSubmit(cleanedData);
      console.log('[CustomerForm] Form submission handler completed');
    } catch (error) {
      console.error('[CustomerForm] Error in form submission:', error);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    console.log('[CustomerForm] Cancel button clicked');
    e.preventDefault();
    e.stopPropagation();
    onCancel(e);
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      className="space-y-6"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Company Name *
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="name"
              id="name"
              required
              defaultValue={customer?.name}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        <div>
          <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
            Contact Name
          </label>
          <div className="mt-1">
            <input
              type="text"
              name="contactName"
              id="contactName"
              defaultValue={customer?.contactName}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <div className="mt-1">
            <input
              type="email"
              name="email"
              id="email"
              defaultValue={customer?.email}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
            Phone
          </label>
          <div className="mt-1">
            <input
              type="tel"
              name="phone"
              id="phone"
              defaultValue={customer?.phone}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700">
            Country *
          </label>
          <div className="mt-1">
            <select
              id="country"
              name="country"
              required
              defaultValue={customer?.country || ''}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">Select a country</option>
              {COUNTRIES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <Button
          type="button"
          variant="secondary"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
          onClick={(e) => e.stopPropagation()}
        >
          {customer ? 'Update Customer' : 'Create Customer'}
        </Button>
      </div>
    </form>
  );
};
